import { Stack } from "@mui/material";
import PageContainer from "components/PageContainer/PageContainer";
import React, { useCallback, useState } from 'react'
import { useAuth } from "utils/useAuth/useAuth";

/**
 * Custom hook to faciliate api error handling and using PageContainer
 */
const usePageWrapper = () => {
  const { logout } = useAuth();

  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [error, setError] = useState(null); // falsey = no error, true = default error, string = error message

  /**
   * @typedef configType
   * @property {number[]} [allowedStatusCodes] - check if status code is in this array, default: [200]
   * @property {number[]} [unauthorizedStatusCodes] - check if status code is in this array, default: [403]
   * @property {string[]} [allowedResCodes] - check if response code is in this array, default: ["0000"]
   * @property {string[]} [unauthorizedResCodes] - check if response code is in this array, default: []
   * @property {boolean} [skipUnauthorized] - skip setting isUgetErrorMessagenauthorized even if status code is in unauthorizedStatusCodes, default: false
   * @property {(resCode: number | string | null) => string | boolean} [getErrorMessage] - get error message from status code or response code, will not set error if this function returns a falsy value
   * 
   */
  /** 
   * @type {(res: import("axios").AxiosResponse<any>, config?: configType) => boolean}
   */
  const handleApi = useCallback((res, config = {}) => {
    const defaultConfig = {
      allowedStatusCodes: [200],
      unauthorizedStatusCodes: [403],
      allowedResCodes: ["0000"],
      unauthorizedResCodes: [],
      skipUnauthorized: false,
      getErrorMessage: () => false,
    };
    const pConfig = { ...defaultConfig, ...config };
    const { allowedStatusCodes, unauthorizedStatusCodes, allowedResCodes, unauthorizedResCodes, skipUnauthorized, getErrorMessage } = pConfig;

    try {
      if (!res) {
        setError("networkError");
        return false;
      }

      const status = res.status;
      const isInvalidToken = status === 403 && res.data?.errorCode === "INVALID_TOKEN";
      if ((status === 401) || isInvalidToken) {
        logout("TOKEN_EXPIRED");
        return false;
      }
      if (!allowedStatusCodes.includes(status)) {
        if (unauthorizedStatusCodes.includes(status)) {
          if (!skipUnauthorized) {
            setIsUnauthorized(true);
            return false;
          }
        } else {
          setError(getErrorMessage(status));
          return false;
        }
      }

      const resCode = res.data?.responseCode ?? null;
      if (!allowedResCodes.includes(resCode)) {
        if (unauthorizedResCodes.includes(resCode)) {
          if (!skipUnauthorized) {
            setIsUnauthorized(true);
            return false;
          }
        } else {
          setError(getErrorMessage(resCode));
          return false;
        }
      }

      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }, [logout]);

  /**
   * @typedef PageWrapperProps
   * @property {boolean} [isLoading=false]
   * @property {boolean} [noPadding=false]
   * @property {import("react").ReactNode} children
   */
  /**
   * @type {import('react').FC<PageWrapperProps>}
   */
  const PageWrapper = useCallback(({ isLoading = false, noPadding = false, children }) => {
    return (
      <Stack sx={{ flexGrow: 1, position: "relative" }}>
        <PageContainer
          loading={!isUnauthorized && isLoading}
          noPadding={noPadding}
          unauthorized={isUnauthorized}
          errorMessage={error}
        >
          {children}
        </PageContainer>
      </Stack>
    );
  }, [isUnauthorized, error]);

  /**
   * @typedef PageOuterContainerProps
   * @property {import("react").ReactNode} children
   */
  /**
   * Use this when the immediate parent is MainLayout
   * 
   * @type {import('react').FC<PageOuterContainerProps>}
   */
  const PageOuterContainer = useCallback(({ children, ...props }) => {
    return (
      <Stack
        direction="column"
        {...props}
        sx={{
          height: "100%",
          width: "100%",
          background: "white",
          overflow: "auto",
          ...props?.sx
        }}
      >
        <Stack
          direction="column"
          sx={{
            flexGrow: 1,
            padding: "24px",
          }}>
          {children}
        </Stack>
      </Stack>
    );
  }, []);

  /**
   * @typedef PageStickyHeaderProps
   * @property {import("react").ReactNode} children
   * @property {boolean} [noPadding=false]
   * @property {boolean} [noBorder=false]
   */
  /**
   * @type {import('react').FC<PageStickyHeaderProps>}
   */
  const PageStickyHeader = useCallback(({ children, noPadding = false, noBorder = false, ...props }) => {
    return (
      <Stack
        direction="column"
        gap={2}
        {...props}
        sx={{
          m: "-24px -24px 24px",
          p: noPadding ? "24px 24px 0px" : "24px",
          position: "sticky",
          top: 0,
          zIndex: 1,
          background: "white",
          borderBottom: noBorder ? "none" : "1px solid #E0E0E0",
          ...props?.sx
        }}
      >
        {children}
      </Stack>
    );
  }, []);

  /**
   * @typedef PageStickyFooterProps
   * @property {import("react").ReactNode} children
   * @property {boolean} [noPadding=false]
   * 
   */
  /**
   * @type {import('react').FC<PageStickyFooterProps>}
   */
  const PageStickyFooter = useCallback(({ children, noPadding = false, ...props }) => {
    return (
      <Stack
        direction="column"
        justifyContent="stretch"
        gap={2}
        {...props}
        sx={{
          m: "0px -24px -24px",
          p: noPadding ? "0 24px 24px" : "24px",
          position: "sticky",
          bottom: 0,
          zIndex: 1,
          background: "white",
          ...props?.sx
        }}
      >
        {children}
      </Stack>
    );
  }, []);

  return {
    handleApi: handleApi,
    PageWrapper: PageWrapper,
    PageOuterContainer: PageOuterContainer,
    PageStickyHeader: PageStickyHeader,
    PageStickyFooter: PageStickyFooter
  }
}

export default usePageWrapper